import { PropsWithChildren } from 'react';

import { cn } from '@components/primitives/utilities';

import { HeaderSize, useHeaderContext } from './context';

import css from './styles.module.css';

type HeadingLevel = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/** The document level each size is usually used at. */
const levelMap: Record<HeaderSize, HeadingLevel> = {
  lg: 'h1',
  md: 'h2',
  sm: 'h3',
};

/**
 * The type scale, which is independent of the document level: a section header
 * can be an `h1` and still be typeset smaller than the page title.
 */
const scaleMap: Record<HeaderSize, string> = {
  lg: 'h2',
  md: 'h4',
  sm: 'h5',
};

export interface HeaderTitleProps extends PropsWithChildren {
  /**
   * Overrides the heading level the size implies, for headers that sit at an
   * unusual document level. The type scale is unaffected.
   */
  as?: HeadingLevel;
  className?: string;
}

export function HeaderTitle({ as, className, children }: HeaderTitleProps) {
  const { size } = useHeaderContext();
  const Component = as ?? levelMap[size];

  return (
    <Component className={cn(css.title, scaleMap[size], className)}>
      {children}
    </Component>
  );
}
